import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ExtendedCreateProjectData } from '@/types';
import {
  configurationService,
  ProjectDirectory as ConfigProjectDirectory,
} from '@/services/configurationService';
import { CheckCircle, Circle, Folder, FolderOpen, Search } from 'lucide-react';

interface DirectoryStepProps {
  data: Partial<ExtendedCreateProjectData>;
  updateData: (data: Partial<ExtendedCreateProjectData>) => void;
  setValid: (isValid: boolean) => void;
}

const DirectoryStep: React.FC<DirectoryStepProps> = ({ data, updateData, setValid }) => {
  const { t } = useTranslation();

  // State management
  const [availableDirectories, setAvailableDirectories] = useState<ConfigProjectDirectory[]>([]);
  const [isLoadingDirectories, setIsLoadingDirectories] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Load directories from configuration service
  useEffect(() => {
    const loadDirectories = async () => {
      setIsLoadingDirectories(true);
      try {
        const directories = await configurationService.getProjectDirectories();
        setAvailableDirectories(directories);
      } catch (error) {
        console.error('Failed to load project directories:', error);
      } finally {
        setIsLoadingDirectories(false);
      }
    };

    loadDirectories();
  }, []);

  // Get selected directories
  const selectedDirectories = data.directories || [];

  // Filter directories by search term
  const filteredDirectories = useMemo(() => {
    if (!searchTerm.trim()) return availableDirectories;

    const searchLower = searchTerm.toLowerCase();
    return availableDirectories.filter(
      directory =>
        directory.name.toLowerCase().includes(searchLower) ||
        directory.description.toLowerCase().includes(searchLower) ||
        directory.tags.some(tag => tag.toLowerCase().includes(searchLower))
    );
  }, [availableDirectories, searchTerm]);

  // Separate default and additional directories
  const defaultDirectories = useMemo(
    () => filteredDirectories.filter(dir => dir.isDefault),
    [filteredDirectories]
  );

  const additionalDirectories = useMemo(
    () => filteredDirectories.filter(dir => !dir.isDefault),
    [filteredDirectories]
  );

  // Group additional directories by tags
  const directoriesByTag = useMemo(() => {
    const grouped: Record<string, ConfigProjectDirectory[]> = {};

    additionalDirectories.forEach(directory => {
      directory.tags.forEach(tag => {
        if (!grouped[tag]) {
          grouped[tag] = [];
        }
        grouped[tag].push(directory);
      });
    });

    return grouped;
  }, [additionalDirectories]);

  // Get unique tags
  const availableTags = useMemo(() => {
    const tags = new Set<string>();
    additionalDirectories.forEach(dir => {
      dir.tags.forEach(tag => tags.add(tag));
    });
    return Array.from(tags).sort();
  }, [additionalDirectories]);

  // Validation effect
  useEffect(() => {
    // Always valid since directories are optional (defaults are automatically included)
    setValid(true);
  }, []); // Removed setValid from dependencies, directories are optional

  // Add all default directories automatically
  useEffect(() => {
    const defaultDirectoryIds = defaultDirectories.map(dir => dir.id);
    const currentDirectories = selectedDirectories.slice();
    let directoriesAdded = false;

    defaultDirectoryIds.forEach(dirId => {
      if (!currentDirectories.includes(dirId)) {
        currentDirectories.push(dirId);
        directoriesAdded = true;
      }
    });

    if (directoriesAdded) {
      updateData({ directories: currentDirectories });
    }
  }, [defaultDirectories, selectedDirectories, updateData]);

  // Update selected directories
  const handleDirectoryToggle = useCallback(
    (directoryId: string) => {
      const currentDirectories = selectedDirectories.slice();
      const directoryIndex = currentDirectories.indexOf(directoryId);

      if (directoryIndex > -1) {
        // Remove directory (only if not default)
        const directory = availableDirectories.find(d => d.id === directoryId);
        if (!directory?.isDefault) {
          currentDirectories.splice(directoryIndex, 1);
        }
      } else {
        // Add directory
        currentDirectories.push(directoryId);
      }

      updateData({ directories: currentDirectories });
    },
    [selectedDirectories, availableDirectories, updateData]
  );

  // Select all directories in tag
  const handleSelectAllInTag = useCallback(
    (tag: string) => {
      const tagDirectories = directoriesByTag[tag] || [];
      const currentDirectories = [...selectedDirectories];

      tagDirectories.forEach(directory => {
        if (!currentDirectories.includes(directory.id)) {
          currentDirectories.push(directory.id);
        }
      });

      updateData({ directories: currentDirectories });
    },
    [selectedDirectories, directoriesByTag, updateData]
  );

  // Get tag color
  const getTagColor = (tag: string) => {
    const colors = {
      general: 'bg-gray-100 text-gray-800',
      legal: 'bg-brand-100 text-brand-800',
      admin: 'bg-purple-100 text-purple-800',
      design: 'bg-green-100 text-green-800',
      technical: 'bg-cyan-100 text-cyan-800',
      construction: 'bg-red-100 text-red-800',
      progress: 'bg-yellow-100 text-yellow-800',
      quality: 'bg-red-100 text-red-800',
      safety: 'bg-red-100 text-red-800',
      compliance: 'bg-indigo-100 text-indigo-800',
      communication: 'bg-pink-100 text-pink-800',
      financial: 'bg-emerald-100 text-emerald-800',
      closeout: 'bg-violet-100 text-violet-800',
      documentation: 'bg-slate-100 text-slate-800',
    };
    return colors[tag as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6 font-['Inter',sans-serif]">
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        `}
      </style>

      {/* Header */}
      <div className="bg-gradient-to-br from-[var(--brand-primary-light)] to-[#FFEAB8] border border-[var(--brand-primary)] border-opacity-30 rounded-lg p-6 shadow-sm">
        <div className="flex items-start space-x-3 mb-4">
          <div className="w-10 h-10 bg-[var(--brand-primary)] rounded-lg flex items-center justify-center flex-shrink-0">
            <Folder className="h-5 w-5 text-white" />
          </div>
          <div>
            <h4 className="text-lg font-bold text-[#1F1F1F] leading-[1.4em]">
              {t('projects.creation.directories.title')}
            </h4>
            <p className="text-sm text-[#6E7077] mt-1 font-normal leading-[1.75em]">
              {t('projects.creation.directories.description')}
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <input
            type="text"
            placeholder={t('common.search')}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-[var(--brand-primary)] border-opacity-30 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)] focus:ring-opacity-20 font-normal text-sm"
          />
          <Search className="absolute right-3 top-2.5 h-4 w-4 text-[#6E7077]" />
        </div>
      </div>

      {/* Loading State */}
      {isLoadingDirectories ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--brand-primary)] mx-auto"></div>
          <p className="mt-2 text-sm text-[#6E7077]">{t('common.loading')}</p>
        </div>
      ) : (
        <>
          {/* Default Directories */}
          {defaultDirectories.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-8 h-8 bg-brand-100 rounded-lg flex items-center justify-center">
                  <FolderOpen className="h-4 w-4 text-brand-primary" />
                </div>
                <div>
                  <h5 className="text-base font-bold text-[#1F1F1F] leading-[1.4em]">
                    {t('projects.creation.directories.defaultDirectories')}
                  </h5>
                  <p className="text-sm text-[#6E7077] font-normal leading-[1.75em]">
                    These directories are automatically included in all projects
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {defaultDirectories.map(directory => {
                  return (
                    <div
                      key={directory.id}
                      className="relative p-4 border-2 border-brand-200 bg-brand-50 rounded-lg"
                    >
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 mt-0.5">
                          <CheckCircle className="h-5 w-5 text-brand-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <h6 className="text-sm font-bold text-[#1F1F1F] leading-[1.4em]">
                              {directory.name}
                            </h6>
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-brand-primary text-white uppercase tracking-wide">
                              {t('projects.creation.directories.defaultDirectory')}
                            </span>
                          </div>
                          <p className="text-sm text-[#6E7077] mt-1 font-normal leading-[1.75em]">
                            {directory.description}
                          </p>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {directory.tags.map(tag => (
                              <span
                                key={tag}
                                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getTagColor(tag)}`}
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Additional Directories by Tag */}
          {availableTags.map(tag => {
            const tagDirectories = directoriesByTag[tag] || [];
            const selectedInTag = tagDirectories.filter(dir =>
              selectedDirectories.includes(dir.id)
            );

            return (
              <div key={tag} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-[var(--brand-primary-light)] rounded-lg flex items-center justify-center">
                      <Folder className="h-4 w-4 text-[var(--brand-primary)]" />
                    </div>
                    <div>
                      <h5 className="text-base font-bold text-[#1F1F1F] leading-[1.4em] capitalize">
                        {tag} {t('projects.creation.directories.directories', 'Directories')}
                      </h5>
                      <p className="text-sm text-[#6E7077] font-normal leading-[1.75em]">
                        Optional directories for {tag}-related documents
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-sm text-[#6E7077] font-medium">
                      {selectedInTag.length} / {tagDirectories.length}{' '}
                      {t('common.selected', 'selected')}
                    </span>
                    {selectedInTag.length < tagDirectories.length && (
                      <button
                        onClick={() => handleSelectAllInTag(tag)}
                        className="text-sm font-medium text-[var(--brand-primary)] hover:text-[var(--brand-primary-hover)] transition-colors"
                      >
                        {t('projects.creation.tools.selectAll')}
                      </button>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {tagDirectories.map(directory => {
                    const isSelected = selectedDirectories.includes(directory.id);

                    return (
                      <div
                        key={directory.id}
                        className={`relative p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                          isSelected
                            ? 'border-[var(--brand-primary)] bg-[var(--brand-primary-light)] bg-opacity-30'
                            : 'border-gray-200 hover:border-[var(--brand-primary)] hover:border-opacity-30'
                        }`}
                        onClick={() => handleDirectoryToggle(directory.id)}
                      >
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0 mt-0.5">
                            {isSelected ? (
                              <CheckCircle className="h-5 w-5 text-[var(--brand-primary)]" />
                            ) : (
                              <Circle className="h-5 w-5 text-gray-400" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h6 className="text-sm font-bold text-[#1F1F1F] leading-[1.4em]">
                              {directory.name}
                            </h6>
                            <p className="text-sm text-[#6E7077] mt-1 font-normal leading-[1.75em]">
                              {directory.description}
                            </p>
                            <div className="flex flex-wrap gap-1 mt-2">
                              {directory.tags.map(dirTag => (
                                <span
                                  key={dirTag}
                                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getTagColor(dirTag)}`}
                                >
                                  {dirTag}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Selected Directories Summary */}
          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
            <h5 className="text-base font-bold text-[#1F1F1F] leading-[1.4em] mb-4">
              {t('projects.creation.directories.selectedDirectories')}
            </h5>

            {selectedDirectories.length === 0 ? (
              <div className="text-center py-6">
                <Folder className="mx-auto h-8 w-8 text-[#6E7077]" />
                <h6 className="mt-2 text-sm font-medium text-[#1F1F1F]">
                  {t('projects.creation.directories.noDirectoriesSelected')}
                </h6>
                <p className="mt-1 text-sm text-[#6E7077] font-normal leading-[1.75em]">
                  {t('projects.creation.directories.noDirectoriesDescription')}
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-4">
                  {selectedDirectories.map(directoryId => {
                    const directory = availableDirectories.find(d => d.id === directoryId);
                    if (!directory) return null;

                    return (
                      <div
                        key={directoryId}
                        className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium ${
                          directory.isDefault
                            ? 'bg-brand-100 text-brand-800'
                            : 'bg-[var(--brand-primary-light)] text-[var(--brand-primary)]'
                        }`}
                      >
                        <Folder className="h-3 w-3 flex-shrink-0" />
                        <span className="truncate">{directory.name}</span>
                      </div>
                    );
                  })}
                </div>
                <p className="text-sm text-[#6E7077] font-normal leading-[1.75em]">
                  {t('projects.creation.directories.directoriesSelected', {
                    count: selectedDirectories.length,
                  })}
                </p>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default DirectoryStep;
